import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Table, Form, Input, Select, Switch, Space, message, Popconfirm, Row, Col, Divider, Tag, Descriptions, InputNumber, Alert, Checkbox } from 'antd';
import { EditOutlined, DeleteOutlined, FilePdfOutlined, PlusOutlined, MinusCircleOutlined, ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import DEFINICOES, { NOMES } from './calibracoes';

const { Option } = Select;

function ListaCalibracoes() {
  const [lista, setLista] = useState([]);
  const [filtro, setFiltro] = useState('');
  const navigate = useNavigate();

  useEffect(() => { carregar(); }, []);
  const carregar = async () => {
    try { const r = await api.get('/calibracoes'); setLista(r.data || []); }
    catch { message.error('Erro ao carregar'); }
  };
  const excluir = async (id) => {
    try { await api.delete('/calibracoes/' + id); message.success('Excluída'); carregar(); }
    catch { message.error('Erro ao excluir'); }
  };

  const filtrada = lista.filter((r) => {
    if (!filtro) return true;
    const t = (r.tag || '') + ' ' + (r.razao_social || r.nome_cliente || '') + ' ' + (r.numero_os || '');
    return t.toLowerCase().includes(filtro.toLowerCase());
  });

  const colunas = [
    { title: 'Código', dataIndex: 'codigo_cal', width: 140, render: (v) => v || <span style={{ color: '#999' }}>ao gerar o PDF</span> },
    { title: 'Tipo', dataIndex: 'tipo', width: 170, render: (v) => <Tag color="blue">{NOMES[v] || v}</Tag> },
    { title: 'TAG', dataIndex: 'tag', width: 110 },
    { title: 'Cliente', render: (_, r) => r.razao_social || r.nome_cliente || '-' },
    { title: 'OS', dataIndex: 'numero_os', width: 100 },
    { title: 'Data', dataIndex: 'data_calibracao', width: 110, render: (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '-' },
    {
      title: 'Ações', width: 120, render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => navigate('/calibracoes/' + r.id)} />
          <Popconfirm title="Excluir?" onConfirm={() => excluir(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card title="Calibrações" extra={<Input placeholder="Buscar TAG, cliente ou OS" value={filtro} onChange={(e) => setFiltro(e.target.value)} style={{ width: 280 }} allowClear />}>
      <div style={{ marginBottom: 12, color: '#888', fontSize: 12 }}>
        As calibrações são criadas a partir da OS, em Criar Laudo &rarr; Calibração.
      </div>
      <Table rowKey="id" columns={colunas} dataSource={filtrada} size="small" pagination={{ pageSize: 20 }} />
    </Card>
  );
}

function campoItem(c, form) {
  if (c.t === 'calc') {
    return (
      <Form.Item label={c.l} tooltip={c.ajuda}>
        <Form.Item noStyle shouldUpdate>
          {() => <Input disabled value={c.calc(form.getFieldsValue()) || ''} />}
        </Form.Item>
      </Form.Item>
    );
  }
  if (c.t === 'bool') {
    return <Form.Item name={c.n} label={c.l} tooltip={c.ajuda} valuePropName="checked"><Switch /></Form.Item>;
  }
  if (c.sugestao) {
    return (
      <Form.Item label={c.l} tooltip={c.ajuda} style={{ marginBottom: 4 }}>
        <Form.Item name={c.n} noStyle>
          <Select allowClear>{(c.o || []).map((o) => <Option key={String(o[0])} value={o[0]}>{o[1]}</Option>)}</Select>
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {() => {
            const sug = c.sugestao(form.getFieldsValue());
            if (!sug) return null;
            return (
              <div style={{ fontSize: 11, marginTop: 2, color: '#888' }}>
                Sugestão: <a onClick={() => form.setFieldsValue({ [c.n]: sug })}>{sug}</a>
              </div>
            );
          }}
        </Form.Item>
      </Form.Item>
    );
  }
  let ctrl;
  if (c.t === 'num') ctrl = <InputNumber style={{ width: '100%' }} decimalSeparator="," controls={false} />;
  else if (c.t === 'data') ctrl = <Input type="date" />;
  else if (c.t === 'area') ctrl = <Input.TextArea rows={c.rows || 2} />;
  else if (c.t === 'sel') {
    ctrl = (
      <Select allowClear={!c.fixo} disabled={c.fixo}>
        {(c.o || []).map((o) => <Option key={String(o[0])} value={o[0]}>{o[1]}</Option>)}
      </Select>
    );
  } else ctrl = <Input disabled={c.fixo} />;

  const extra = {};
  if (c.up) extra.normalize = (v) => (v || '').toUpperCase().replace(/^\s+/, '');
  return <Form.Item name={c.n} label={c.l} tooltip={c.ajuda} {...extra}>{ctrl}</Form.Item>;
}

function CalibracaoForm({ id }) {
  const [reg, setReg] = useState(null);
  const [padroes, setPadroes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usarCnpj, setUsarCnpj] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [padraoSel, setPadraoSel] = useState(undefined);
  const [form] = Form.useForm();
  const baseCal = useRef(null);
  useAvisoAlteracoes(() => baseCal.current !== null && JSON.stringify(form.getFieldsValue(true)) !== baseCal.current);
  const navigate = useNavigate();

  useEffect(() => { carregar(); }, [id]);

  const carregar = async () => {
    try {
      const r = await api.get('/calibracoes/' + id);
      const d = r.data;
      setReg(d);
      const v = Object.assign({}, d);
      if (v.data_calibracao) v.data_calibracao = String(v.data_calibracao).slice(0, 10);
      const defTipo = DEFINICOES[d.tipo];
      if (!Array.isArray(v.pontos) || !v.pontos.length) {
        v.pontos = (defTipo && defTipo.pontosPadrao) ? defTipo.pontosPadrao.map((x) => Object.assign({}, x)) : [{}, {}, {}];
      }
      if (!v.norma_referencia) v.norma_referencia = 'NBR 14.105';
      if (!v.fator_k) v.fator_k = 2;
      setUsarCnpj(!!d.usar_endereco_cnpj);
      if (!v.validade_anos) v.validade_anos = 1;
      form.setFieldsValue(v);
      baseCal.current = JSON.stringify(form.getFieldsValue(true));
      try {
        const pr = await api.get('/padroes-calibracao');
        const lista = pr.data || [];
        setPadroes(lista);
        const atual = d.padrao_identificacao
          ? lista.find((x) => String(x.identificacao || '').trim() === String(d.padrao_identificacao).trim())
          : null;
        setPadraoSel(atual ? atual.id : undefined);
      } catch (e) { }
    } catch { message.error('Erro ao carregar a calibração'); }
  };

  const salvar = async (silencioso) => {
    try {
      setLoading(true);
      const v = await form.validateFields();
      await api.put('/calibracoes/' + id, Object.assign({}, reg, v));
      if (!silencioso) message.success('Calibração salva!');
      await carregar();
      return true;
    } catch (e) {
      if (!(e && e.errorFields)) message.error('Erro ao salvar');
      return false;
    } finally { setLoading(false); }
  };

  const gerarPDF = async () => {
    setGerando(true);
    message.loading({ content: 'Gerando o laudo em PDF, aguarde...', key: 'genpdf', duration: 0 });
    const ok = await salvar(true);
    if (!ok) { setGerando(false); message.destroy('genpdf'); return; }
    try {
      const resp = await api.get('/calibracoes/' + id + '/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      message.success({ content: 'Laudo gerado e salvo.', key: 'genpdf', duration: 3 });
      setGerando(false);
      navigate('/clientes/' + reg.cliente_id);
    } catch {
      setGerando(false);
      message.error({ content: 'Erro ao gerar PDF', key: 'genpdf' });
    }
  };

  const usarPadrao = (pid) => {
    const p = padroes.find((x) => String(x.id) === String(pid));
    if (!p) return;
    form.setFieldsValue({
      padrao_identificacao: p.identificacao, padrao_marca: p.marca, padrao_certificado: p.certificado,
      padrao_calibrado_por: p.calibrado_por, padrao_validade: p.validade
    });
  };

  if (!reg) return <Card loading />;
  const def = DEFINICOES[reg.tipo];

  const cabecalho = (
    <Descriptions size="small" bordered column={4} style={{ marginBottom: 16 }}>
      <Descriptions.Item label="Cliente" span={2}>{reg.razao_social || reg.nome_cliente || '-'}</Descriptions.Item>
      <Descriptions.Item label="OS">{reg.numero_os || '-'}</Descriptions.Item>
      <Descriptions.Item label="Técnico">{reg.tecnico || reg.os_tecnico || '-'}</Descriptions.Item>
      <Descriptions.Item label="CNPJ faturamento">{reg.cnpj_faturamento || reg.cnpj || '-'}</Descriptions.Item>
      <Descriptions.Item label="Norma">NBR 14.105</Descriptions.Item>
      <Descriptions.Item label="Código do laudo">{reg.codigo_cal || <span style={{ color: '#999' }}>gerado ao emitir o PDF</span>}</Descriptions.Item>
      <Descriptions.Item label="Endereço de atendimento" span={4}>
        <Space>
          <span>
            {usarCnpj
              ? (reg.cnpj_logradouro
                  ? reg.cnpj_logradouro + ', ' + (reg.cnpj_numero || 'S/N') + ' - ' + (reg.cnpj_bairro || '') + ', ' + (reg.cnpj_cidade || '') + '/' + (reg.cnpj_estado || '')
                  : 'CNPJ sem endereço cadastrado')
              : (reg.end_atend_logradouro
                  ? reg.end_atend_logradouro + ', ' + (reg.end_atend_numero || 'S/N') + ' - ' + (reg.end_atend_bairro || '') + ', ' + (reg.end_atend_cidade || '') + '/' + (reg.end_atend_estado || '')
                  : 'não informado na OS')}
          </span>
          <Checkbox checked={usarCnpj} onChange={(e) => { setUsarCnpj(e.target.checked); form.setFieldsValue({ usar_endereco_cnpj: e.target.checked }); }}>
            Usar endereço do CNPJ
          </Checkbox>
        </Space>
      </Descriptions.Item>
    </Descriptions>
  );

  if (!def) {
    return (
      <Card title={'Calibração de ' + (NOMES[reg.tipo] || reg.tipo)} extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Voltar</Button>}>
        {cabecalho}
        <Alert type="info" showIcon message="Formulário em construção"
          description="Este tipo de calibração ainda não tem formulário próprio. O registro já está criado e ligado à OS." />
      </Card>
    );
  }

  return (
    <Card title={'Calibração de ' + def.titulo}>
      {cabecalho}

      <Form form={form} layout="vertical" size="small">
        <Form.Item name="padrao_identificacao" hidden><Input /></Form.Item>
        <Form.Item name="padrao_marca" hidden><Input /></Form.Item>
        <Form.Item name="padrao_certificado" hidden><Input /></Form.Item>
        <Form.Item name="padrao_calibrado_por" hidden><Input /></Form.Item>
        <Form.Item name="padrao_validade" hidden><Input /></Form.Item>
        <Form.Item name="norma_referencia" hidden><Input /></Form.Item>
        <Form.Item name="usar_endereco_cnpj" hidden valuePropName="checked"><Checkbox /></Form.Item>

        {def.secoes.map((sec) => (
          <div key={sec.titulo}>
            <Divider orientation="left" style={{ margin: '0 0 12px' }}>{sec.titulo}</Divider>
            <Row gutter={12}>
              {sec.campos.map((c) => <Col span={c.span || 6} key={c.n}>{campoItem(c, form)}</Col>)}
            </Row>
          </div>
        ))}

        <Divider orientation="left" style={{ margin: '4px 0 12px' }}>
          Pontos de calibração
          <span style={{ fontWeight: 400, fontSize: 12, color: '#888', marginLeft: 10 }}>{def.pontos.ajuda}</span>
        </Divider>

        <Form.List name="pontos">
          {(fields, { add, remove }) => (
            <>
              {fields.map((f, i) => (
                <Row gutter={8} key={f.key}>
                  <Col span={2}><div style={{ paddingTop: i === 0 ? 30 : 6, fontSize: 12, color: '#888' }}>Ponto {i + 1}</div></Col>
                  {def.pontos.colunas.map((col) => (
                    <Col span={6} key={col.n}>
                      <Form.Item {...f} name={[f.name, col.n]} label={i === 0 ? col.l : null} tooltip={i === 0 ? col.ajuda : null}>
                        {col.t === 'texto'
                          ? <Input />
                          : <InputNumber style={{ width: '100%' }} decimalSeparator="," controls={false} />}
                      </Form.Item>
                    </Col>
                  ))}
                  <Col span={2}>
                    <div style={{ paddingTop: i === 0 ? 28 : 2 }}>
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(f.name)} />
                    </div>
                  </Col>
                </Row>
              ))}
              {(!def.maxPontos || fields.length < def.maxPontos) &&
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} style={{ marginBottom: 12 }}>Adicionar ponto</Button>}
              {def.maxPontos && fields.length >= def.maxPontos &&
                <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>O laudo deste instrumento imprime no máximo {def.maxPontos} pontos.</div>}
            </>
          )}
        </Form.List>

        <Divider orientation="left" style={{ margin: '4px 0 12px' }}>{def.semPadrao ? 'Fechamento' : 'Padrão utilizado e fechamento'}</Divider>
        <Row gutter={12}>
          {!def.semPadrao && <Col span={8}>
            <Form.Item label="Instrumento padrão" tooltip="Padrão usado na comparação. Os dados dele saem automaticamente no laudo.">
              <Select allowClear placeholder="Selecione o padrão" value={padraoSel}
                onChange={(v) => { setPadraoSel(v); usarPadrao(v); }}>
                {padroes.map((p) => <Option key={p.id} value={p.id}>{(p.tipo || '') + ' — ' + (p.identificacao || '')}</Option>)}
              </Select>
            </Form.Item>
          </Col>}
          {def.fechamento.map((c) => <Col span={c.span || 4} key={c.n}>{campoItem(c, form)}</Col>)}
        </Row>

        <Form.Item name="observacoes" label="Observações" tooltip="Texto livre que aparece num bloco próprio do laudo. Deixe vazio se não houver."><Input.TextArea rows={2} /></Form.Item>
        <Form.Item name="resumo" label="Resumo da atividade" tooltip="Em branco, o laudo usa o texto padrão da Celum para este tipo de calibração."><Input.TextArea rows={3} /></Form.Item>

        <Divider style={{ margin: '16px 0' }} />
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Voltar</Button>
          <Button icon={<SaveOutlined />} onClick={salvar} loading={loading}>Salvar</Button>
          <Button type="primary" icon={<FilePdfOutlined />} onClick={gerarPDF} loading={gerando} disabled={gerando}>
            {gerando ? 'Gerando PDF...' : 'Salvar e gerar PDF'}
          </Button>
        </Space>
      </Form>
    </Card>
  );
}

function Calibracoes() {
  const { id } = useParams();
  return id ? <CalibracaoForm id={id} /> : <ListaCalibracoes />;
}

export default Calibracoes;
